(() => {
  'use strict';

  window.theme.variables = { productPageSticky: false };

  if (customElements.get('product-sticky')) return;

  const STICKY_GAP_PX = 16;

  customElements.define(
    'product-sticky',
    class ProductSticky extends HTMLElement {
      constructor() {
        super();
        this.stickyScrollTop = 0;
        this.scrollLastPosition = 0;
        this.stickyDefaultTop = 0;
        this.currentPoint = 0;
        this.defaultTopBottomSpacings = 30;
        this.scrollTopPosition = window.scrollY;
        this.scrollDirectionDown = true;
        this.requestAnimationSticky = null;
        this.stickyFormLoad = true;
        this.stickyFormLastHeight = null;
        this.onChangeCounter = 0;
        this.isSplitPdp = false;
        this.lastHasScrolled = document.body.classList.contains('has-scrolled');
        this.scrollEvent = (event) => this.scrollEvents(event);
        this.resizeEvent = (event) => this.resizeEvents(event);
        this.stickyHeightEvent = () => this.onStickyHeaderHeightChange();
      }

      connectedCallback() {
        this.isSplitPdp = Boolean(
          this.closest('product-info')?.hasAttribute('data-split-pdp'),
        );
        this.stickyScrollCheck();
        document.addEventListener('theme:resize', this.resizeEvent);
        document.addEventListener(
          'theme:header:sticky-height',
          this.stickyHeightEvent,
        );
        this.initSticky();
      }

      initSticky() {
        if (!theme.variables.productPageSticky) return;

        this.requestAnimationSticky = requestAnimationFrame(() =>
          this.calculateStickyPosition(),
        );

        if (this.isSplitPdp) {
          document.addEventListener('theme:scroll', this.scrollEvent);
          return;
        }

        this.addEventListener('theme:form:sticky', (event) => {
          this.removeAnimationFrame();
          this.requestAnimationSticky = requestAnimationFrame(() =>
            this.calculateStickyPosition(event),
          );
        });
        document.addEventListener('theme:scroll', this.scrollEvent);
      }

      scrollEvents(event) {
        if (event.detail !== null) {
          this.scrollTopPosition = event.detail.position;
          this.scrollDirectionDown = event.detail.down;
        }

        const hasScrolled = document.body.classList.contains('has-scrolled');
        if (hasScrolled !== this.lastHasScrolled) {
          this.lastHasScrolled = hasScrolled;
          this.stickyFormLoad = true;
        }

        if (this.isSplitPdp) {
          if (this.stickyFormLoad && !this.requestAnimationSticky) {
            this.requestAnimationSticky = requestAnimationFrame(() =>
              this.calculateStickyPosition(),
            );
          }
          return;
        }

        if (!this.requestAnimationSticky) {
          this.requestAnimationSticky = requestAnimationFrame(() =>
            this.calculateStickyPosition(),
          );
        }
      }

      resizeEvents() {
        this.stickyFormLoad = true;
        this.stickyScrollCheck();
        document.removeEventListener('theme:scroll', this.scrollEvent);
        this.initSticky();
      }

      onStickyHeaderHeightChange() {
        if (!theme.variables.productPageSticky) return;
        this.stickyFormLoad = true;
        this.removeAnimationFrame();
        this.requestAnimationSticky = requestAnimationFrame(() =>
          this.calculateStickyPosition(),
        );
      }

      stickyScrollCheck() {
        if (window.theme.getWindowWidth() < window.theme.sizes.large) {
          theme.variables.productPageSticky = false;
          this.classList.remove('is-sticky');
          return;
        }

        const productInfo = this.closest('product-info');
        const mediaSlider = productInfo?.querySelector(
          '[data-product-single-media-slider]',
        );
        const stickyBoundary = productInfo?.hasAttribute('data-split-pdp')
          ? productInfo
          : mediaSlider;

        if (!stickyBoundary) return;

        const shouldStick = this.offsetHeight < stickyBoundary.offsetHeight;
        theme.variables.productPageSticky = shouldStick;
        this.classList.toggle('is-sticky', shouldStick);
      }

      getStickyHeaderOffset() {
        const stickyHeader = document.querySelector('[data-header-sticky]');
        if (!stickyHeader) return this.defaultTopBottomSpacings;

        const fromWindow =
          typeof window.stickyHeaderHeight === 'number'
            ? window.stickyHeaderHeight
            : 0;
        const fromCss = Number.parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--header-sticky-height')
            .trim(),
          10,
        );
        const fromMeasure = Math.round(
          stickyHeader.getBoundingClientRect().height,
        );

        const headerOffset = Math.max(
          fromWindow || 0,
          Number.isFinite(fromCss) ? fromCss : 0,
          fromMeasure || 0,
        );

        return headerOffset + STICKY_GAP_PX;
      }

      calculateStickyPosition(event = null) {
        if (this.isSplitPdp) {
          const stickyTop = this.getStickyHeaderOffset();

          this.stickyDefaultTop = stickyTop;
          this.stickyScrollTop = stickyTop;
          this.currentPoint = stickyTop;
          this.scrollLastPosition = window.scrollY;
          this.stickyFormLoad = false;
          this.style.setProperty('--sticky-top', `${stickyTop}px`);
          this.removeAnimationFrame();
          return;
        }

        const eventHasDetails = Boolean(event?.detail);
        const isAccordionEvent = Boolean(
          eventHasDetails && event.detail.element === 'accordion',
        );
        const formHeight = this.offsetHeight;
        const bottomPosition =
          window.innerHeight - formHeight - this.defaultTopBottomSpacings;
        const scrollDistance = Math.abs(
          this.scrollTopPosition - this.scrollLastPosition,
        );

        this.stickyScrollTop += this.scrollDirectionDown
          ? -scrollDistance
          : scrollDistance;

        if (this.stickyFormLoad) {
          this.stickyDefaultTop = this.getStickyHeaderOffset();
          this.stickyScrollTop = this.stickyDefaultTop;
        }

        this.stickyScrollTop = Math.min(
          Math.max(this.stickyScrollTop, bottomPosition),
          this.stickyDefaultTop,
        );

        const distanceToPoint = this.stickyScrollTop - this.currentPoint;
        this.currentPoint = this.stickyFormLoad
          ? this.stickyScrollTop
          : this.currentPoint + 0.5 * distanceToPoint;
        this.style.setProperty('--sticky-top', `${this.currentPoint}px`);
        this.scrollLastPosition = this.scrollTopPosition;
        this.stickyFormLoad = false;

        const shouldContinue =
          (isAccordionEvent && this.onChangeCounter <= 10) ||
          (isAccordionEvent && this.stickyFormLastHeight !== formHeight) ||
          (this.stickyScrollTop !== this.currentPoint &&
            this.requestAnimationSticky);

        if (shouldContinue) {
          if (isAccordionEvent) this.onChangeCounter += 1;
          if (isAccordionEvent && this.stickyFormLastHeight !== formHeight) {
            this.onChangeCounter = 11;
          }
          this.requestAnimationSticky = requestAnimationFrame(() =>
            this.calculateStickyPosition(event),
          );
        } else if (this.requestAnimationSticky) {
          this.removeAnimationFrame();
        }

        this.stickyFormLastHeight = formHeight;
      }

      removeAnimationFrame() {
        if (!this.requestAnimationSticky) return;
        cancelAnimationFrame(this.requestAnimationSticky);
        this.requestAnimationSticky = null;
        this.onChangeCounter = 0;
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize', this.resizeEvent);
        document.removeEventListener('theme:scroll', this.scrollEvent);
        document.removeEventListener(
          'theme:header:sticky-height',
          this.stickyHeightEvent,
        );
        this.removeAnimationFrame();
      }
    },
  );
})();
